import { normalizeDaywisePlan } from "./normalizeTimeSlot";

function htmlToList(html) {
  if (!html) return [];
  const doc = new DOMParser().parseFromString(html, "text/html");
  const clean = (s) => s.replace(/\u00A0/g, " ").trim();
  const items = Array.from(doc.querySelectorAll("li"))
    .map((li) => clean(li.textContent))
    .filter(Boolean);
  if (items.length) return items;
  return doc.body.textContent.split("\n").map(clean).filter(Boolean);
}

function mapSlot(slot) {
  const entryType = slot.entryType || "activity";
  const isFlight =
    entryType === "transport" &&
    slot.transportOption?.vehicaltype === "Flight";

  if (isFlight) {
    return {
      type: "flight",
      title: slot.title || slot.transportOption?.label || "Flight",
      time: slot.time || "",
    };
  }

  if (entryType === "transport") {
    const tType = slot.transportType
      ? slot.transportType.charAt(0).toUpperCase() + slot.transportType.slice(1)
      : "Transport";
    const tTitle = slot.title || "";
    const vType = slot.transportOption?.vehicaltype || slot.transportOption?.label || "";

    return {
      type: "transfer",
      title: [tType, tTitle, vType].filter(Boolean).join(" . "),
      subtitle: "",
      time: slot.time || "",
    };
  }

  if (entryType === "hotel") {
    const raw = slot.hotelName?.hotel_image;
    const image = Array.isArray(raw) ? raw[0] || "" : raw || "";
    const hotelTypeText = slot.hotelType?.hoteltype || "";
    const starMatch = hotelTypeText.match(/\d/);
    const rating = starMatch ? parseInt(starMatch[0], 10) : 0;

    return {
			type: "hotel",
			title: slot.hotelName?.label || slot.title || "Hotel Stay",
			subtitle: slot.title || "",
			description: [
				slot.hotelOptions?.label,
				slot.noofRooms
					? `${slot.noofRooms} Room${slot.noofRooms > 1 ? "s" : ""}`
					: null,
			]
				.filter(Boolean)
				.join(" · "),
			hotelType: hotelTypeText,
			rating,
			image,
			time: slot.time || "",
			mealPlan: "",
		};
  }

  if (entryType === "meal") {
    return {
      type: "meal",
      title: slot.title || "Meal",
      time: slot.time || "",
    };
  }

  const desc = slot.description
    ? slot.description.replace(/<[^>]+>/g, "")
    : "";
  return {
    type: "activity",
    title: slot.title || "",
    subtitle: slot.title || "",
    description: desc.length > 300 ? desc.slice(0, 300) + "…" : desc,
    image: slot.image || "",
    time: slot.time || "",
  };
}

function fmt(iso) {
  if (!iso) return "";
  try {
    return new Date(iso).toLocaleDateString("en-IN", {
      day: "numeric",
      month: "short",
      year: "numeric",
    });
  } catch {
    return "";
  }
}

function addDays(iso, n) {
  if (!iso) return "";
  try {
    const d = new Date(iso);
    const numDays = Number(n);
    if (isNaN(numDays)) return "";
    d.setDate(d.getDate() + numDays - 1);
    return d.toLocaleDateString("en-IN", {
      day: "numeric",
      month: "short",
      year: "numeric",
    });
  } catch {
    return "";
  }
}

export function computeCostBreakdown(apiData) {
  const pricePerHead = Number(apiData.price_perhead || 0);
  if (!pricePerHead) return null;

  const parseField = (val) => {
    if (!val) return [];
    if (Array.isArray(val)) return val;
    try { return JSON.parse(val); } catch { return []; }
  };

  const onward = parseField(apiData.transportation);
  const returnT = parseField(apiData.return_transportation);

  let flightCost = 0;

  for (const entry of onward) {
    const t = entry.selectedOnwardTransport;
    if (t?.vehicaltype === "Flight") {
      flightCost += Number(t.pph || 0) + Number(t.overheadprice || 0);
    }
  }

  for (const entry of returnT) {
    const t = entry.selectedReturnTransport;
    if (t?.vehicaltype === "Flight") {
      flightCost += Number(t.pph || 0) + Number(t.overheadprice || 0);
    }
  }

  return {
    landCost: Math.max(0, pricePerHead - flightCost),
    flightCost,
    total: pricePerHead,
    hasFlight: flightCost > 0,
  };
}

export function mapToPdfData(apiData, type) {
  const days = normalizeDaywisePlan(apiData.daywise_plan || []);

  const startDate =
    type === "itinerary" ? fmt(apiData.dateoftravel) : "";
  const endDate =
    type === "itinerary" && apiData.dateoftravel
      ? addDays(apiData.dateoftravel, apiData.noofdays || days.length || 1)
      : "";
  const pax = apiData.noofpax || apiData.pax || "";

  return {
    packageImage: apiData.package_image || "",
    packageName: apiData.packagename || apiData.itineraryname || "",
    subtitle: apiData.packageType || apiData.packagetype || "",
    duration: {
      days: apiData.noofdays || days.length || 0,
      nights: apiData.noofnights || 0,
    },
    pax: pax ? `${pax} Pax` : "",
    schoolName: apiData.schoolname || apiData.schoolName || "",
    startDate,
    endDate,
    days: days.map((day) => ({
      dayNumber: day.dayNumber,
      date: "",
      title: day.planName || `Day ${day.dayNumber}`,
      entries: (day.timeSlots || []).map(mapSlot),
    })),
    inclusions: htmlToList(apiData.texteditordata?.inclusions || apiData.texteditorData?.inclusions),
    exclusions: htmlToList(apiData.texteditordata?.exclusions || apiData.texteditorData?.exclusions),
    termsAndConditions: apiData.texteditordata?.terms_conditions || apiData.texteditorData?.terms_conditions || "",
    paymentPlan: apiData.texteditordata?.payment_plan || apiData.texteditorData?.payment_plan || "",
    cancellationPolicy: apiData.texteditordata?.cancellation_policy || apiData.texteditorData?.cancellation_policy || "",
    notes: apiData.texteditordata?.notes || apiData.texteditorData?.notes || "",
    learningOutcomes: htmlToList(apiData.texteditordata?.learning_outcomes || apiData.texteditorData?.learning_outcomes),
    costBreakdown: apiData.costBreakdown ?? computeCostBreakdown(apiData),
  };
}
